import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { cancelOrder, fetchOrder, placeOrder } from '../api/orders'
import { getProductBySlug } from '../api/products'
import type {
  ApiOrder,
  ApiProductDetail,
  ExpressConfirmationBody,
  PlaceOrderInput,
} from '../api/types'
import { EXPRESS_SHIPMENT_MODE } from '../constants/express'
import {
  CHECKOUT_PLACING_PHASE,
  CHECKOUT_STEP,
  CHECKOUT_UI,
  ONLINE_PAYMENT_EXPIRED,
  type CheckoutAddonOption,
  type CheckoutFormState,
  type CheckoutPlacingPhase,
  type CheckoutStep,
} from '../constants/orders'
import { CHECKOUT_SETTLE } from '../constants/payments'
import { QUERY_KEYS } from '../constants/query'
import {
  CHECKOUT_SCHEDULE_MODE,
  CHECKOUT_SPLIT_MODE,
  SCHEDULE_KIND,
  SCHEDULE_UI,
  type CheckoutScheduleMode,
  type CheckoutSplitMode,
} from '../constants/schedule'
import { PAYMENT_METHOD } from '../constants/settings'
import { displayAuthName } from '../helpers/auth'
import {
  needsPaymentConfirmation,
  waitForCheckoutPayment,
} from '../helpers/checkout-payment'
import { isValidPincode } from '../helpers/location'
import {
  addressesWithPincode,
  checkoutAddressRecap,
  checkoutCanSchedule,
  checkoutCanScheduleExpress,
  checkoutCanSplit,
  checkoutDeliveryEstimate,
  checkoutLineAddons,
  checkoutShipmentFlags,
  groupCheckoutShipments,
  shipmentItemsLabel,
  uniqueCartProductSlugs,
} from '../helpers/checkout'
import {
  confirmationFromExpressCheck,
  readCheckoutOrders,
  readExpressConfirmation,
} from '../helpers/express'
import {
  buildPlaceOrderBilling,
  buildPlaceOrderDelivery,
  cartLinesToOrderItems,
  checkoutDeliveryAddress,
  couponApplyMessage,
  isBillingFormEmpty,
  isCheckoutFormReady,
  isUnpaidOnlineOrder,
  nationalPhoneDigits,
  onlinePaymentTimeoutMs,
  prependOrders,
} from '../helpers/orders'
import { payOnlineCheckout, sessionFromCheckout } from '../helpers/payment-gateway'
import {
  checkoutPlaceFlags,
  listScheduleDates,
  listScheduleSlots,
} from '../helpers/schedule'
import { defaultCheckoutPayment } from '../helpers/settings'
import { useAuth } from './useAuth'
import { useExpressAvailability } from './useExpressAvailability'
import { useSavedAddresses } from './useSavedAddresses'
import { useCart } from './useCart'
import { useDeliveryLocation } from './useDeliveryLocation'
import { useLocationPincode } from './useLocationPincode'
import { useStorefrontSettings } from './useStorefrontSettings'

async function cancelUnpaidOnlineOrders(current: ApiOrder[]): Promise<ApiOrder[]> {
  return Promise.all(
    current.map(async (order) => {
      if (!isUnpaidOnlineOrder(order)) return order
      try {
        return await cancelOrder(order.id, ONLINE_PAYMENT_EXPIRED)
      } catch {
        try {
          return await fetchOrder(order.id)
        } catch {
          return order
        }
      }
    }),
  )
}

export function useCheckout() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { lines, clear, addAddon } = useCart()
  const { location } = useDeliveryLocation()
  const { pincode: areaPincode, resolving: pincodeResolving } = useLocationPincode(location)
  const { express, checking: expressChecking } = useExpressAvailability(lines, location)
  const { addresses } = useSavedAddresses()
  const { settings } = useStorefrontSettings()
  const [form, setForm] = useState<CheckoutFormState>(() => ({
    recipientName: displayAuthName(user),
    recipientPhone: nationalPhoneDigits(user?.phone),
    address: '',
    pincode: '',
    notes: '',
    paymentMethod: defaultCheckoutPayment(settings.paymentMethod),
    billingSameAsShipping: true,
    billingName: '',
    billingPhone: '',
    billingAddress: '',
    billingCity: '',
    billingPincode: '',
  }))
  const [submitting, setSubmitting] = useState(false)
  const [placingPhase, setPlacingPhase] = useState<CheckoutPlacingPhase | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [couponInput, setCouponInput] = useState('')
  const [couponError, setCouponError] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<ExpressConfirmationBody | null>(
    null,
  )
  const [splitMode, setSplitMode] = useState<CheckoutSplitMode>(
    CHECKOUT_SPLIT_MODE.split,
  )
  const [scheduleMode, setScheduleMode] = useState<CheckoutScheduleMode>(
    CHECKOUT_SCHEDULE_MODE.asap,
  )
  const [scheduledDate, setScheduledDate] = useState('')
  const [scheduledSlot, setScheduledSlot] = useState<string | null>(null)
  const [expressScheduleMode, setExpressScheduleMode] = useState<CheckoutScheduleMode>(
    CHECKOUT_SCHEDULE_MODE.asap,
  )
  const [expressScheduledDate, setExpressScheduledDate] = useState('')
  const [expressScheduledSlot, setExpressScheduledSlot] = useState<string | null>(null)
  const [step, setStep] = useState<CheckoutStep>(CHECKOUT_STEP.address)
  const [placeConfirmOpen, setPlaceConfirmOpen] = useState(false)
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null)
  const selectedAddress =
    addresses.find((address) => address.id === selectedAddressId) ?? null
  const [placedOrders, setPlacedOrders] = useState<ApiOrder[] | null>(null)
  const productSlugs = uniqueCartProductSlugs(lines)
  const productsQuery = useQuery({
    queryKey: QUERY_KEYS.checkoutProducts(productSlugs),
    queryFn: async () => {
      const entries = await Promise.all(
        productSlugs.map(
          async (slug) => [slug, await getProductBySlug(slug)] as const,
        ),
      )
      return new Map<string, ApiProductDetail>(entries)
    },
    enabled: productSlugs.length > 0,
  })
  const lineAddons = useMemo(
    () => checkoutLineAddons(lines, productsQuery.data ?? new Map()),
    [lines, productsQuery.data],
  )
  const groupedShipments = useMemo(
    () =>
      groupCheckoutShipments(
        lines,
        express,
        splitMode,
        expressChecking,
      ),
    [express, expressChecking, lines, splitMode],
  )
  const shipments = groupedShipments
  const canSplit = checkoutCanSplit(express)
  const canSchedule = checkoutCanSchedule(
    groupedShipments,
    splitMode,
    expressChecking,
  )
  const canScheduleExpress = checkoutCanScheduleExpress(
    groupedShipments,
    splitMode,
    expressChecking,
  )
  const { hasExpress, hasStandard } = checkoutShipmentFlags(groupedShipments)
  const expressItems = shipmentItemsLabel(groupedShipments, EXPRESS_SHIPMENT_MODE.express)
  const standardItems = shipmentItemsLabel(groupedShipments, EXPRESS_SHIPMENT_MODE.standard)
  const deliveryEstimate = checkoutDeliveryEstimate({
    checking: expressChecking,
    canSplit,
    splitMode,
    hasExpress,
    hasStandard,
  })
  const shippingAddress = selectedAddress
    ? selectedAddress.address
    : checkoutDeliveryAddress(form.address, location?.label ?? '')
  const shippingCity = location?.city
  const pincodeEditable = !areaPincode && !pincodeResolving
  const shippingPincode = areaPincode || (pincodeEditable ? form.pincode.trim() : '')
  const savedAddresses = addressesWithPincode(addresses, shippingPincode)
  const checkoutForm: CheckoutFormState = {
    ...form,
    address: shippingAddress,
    pincode: shippingPincode,
    recipientName: selectedAddress?.recipientName ?? form.recipientName,
    recipientPhone: selectedAddress?.recipientPhone ?? form.recipientPhone,
  }
  const addressComplete =
    isCheckoutFormReady(checkoutForm, shippingCity) && isValidPincode(shippingPincode)
  const addressRecap = checkoutAddressRecap({
    address: shippingAddress,
    city: shippingCity ?? null,
    pincode: shippingPincode,
  })
  const scheduleDates = listScheduleDates()
  const scheduleSlots = scheduledDate ? listScheduleSlots(scheduledDate) : []
  const expressScheduleDates = listScheduleDates(new Date(), SCHEDULE_KIND.express)
  const expressScheduleSlots = expressScheduledDate
    ? listScheduleSlots(expressScheduledDate, new Date(), SCHEDULE_KIND.express)
    : []

  useEffect(() => {
    if (!canSplit && splitMode !== CHECKOUT_SPLIT_MODE.split) {
      setSplitMode(CHECKOUT_SPLIT_MODE.split)
    }
  }, [canSplit, splitMode])

  useEffect(() => {
    if (!canSchedule && scheduleMode !== CHECKOUT_SCHEDULE_MODE.asap) {
      setScheduleMode(CHECKOUT_SCHEDULE_MODE.asap)
      setScheduledSlot(null)
    }
  }, [canSchedule, scheduleMode])

  useEffect(() => {
    if (!canScheduleExpress && expressScheduleMode !== CHECKOUT_SCHEDULE_MODE.asap) {
      setExpressScheduleMode(CHECKOUT_SCHEDULE_MODE.asap)
      setExpressScheduledSlot(null)
    }
  }, [canScheduleExpress, expressScheduleMode])

  useEffect(() => {
    const dates = listScheduleDates()
    if (dates.length === 0) {
      if (scheduledDate) setScheduledDate('')
      return
    }
    if (!dates.some((date) => date.key === scheduledDate)) {
      setScheduledDate(dates[0].key)
    }
  }, [scheduledDate])

  useEffect(() => {
    if (scheduleMode !== CHECKOUT_SCHEDULE_MODE.scheduled) return
    const slots = scheduledDate ? listScheduleSlots(scheduledDate) : []
    if (slots.length === 0) {
      if (scheduledSlot) setScheduledSlot(null)
      return
    }
    if (!slots.some((slot) => slot.iso === scheduledSlot)) {
      setScheduledSlot(slots[0].iso)
    }
  }, [scheduleMode, scheduledDate, scheduledSlot])

  useEffect(() => {
    const dates = listScheduleDates(new Date(), SCHEDULE_KIND.express)
    if (dates.length === 0) {
      if (expressScheduledDate) setExpressScheduledDate('')
      return
    }
    if (!dates.some((date) => date.key === expressScheduledDate)) {
      setExpressScheduledDate(dates[0].key)
    }
  }, [expressScheduledDate])

  useEffect(() => {
    if (expressScheduleMode !== CHECKOUT_SCHEDULE_MODE.scheduled) return
    const slots = expressScheduledDate
      ? listScheduleSlots(expressScheduledDate, new Date(), SCHEDULE_KIND.express)
      : []
    if (slots.length === 0) {
      if (expressScheduledSlot) setExpressScheduledSlot(null)
      return
    }
    if (!slots.some((slot) => slot.iso === expressScheduledSlot)) {
      setExpressScheduledSlot(slots[0].iso)
    }
  }, [expressScheduleMode, expressScheduledDate, expressScheduledSlot])

  useEffect(() => {
    setForm((current) => {
      if (settings.paymentMethod === PAYMENT_METHOD.both) {
        if (
          current.paymentMethod === PAYMENT_METHOD.cod ||
          current.paymentMethod === PAYMENT_METHOD.online
        ) {
          return current
        }
      }
      const next = defaultCheckoutPayment(settings.paymentMethod)
      if (current.paymentMethod === next) return current
      return { ...current, paymentMethod: next }
    })
  }, [settings.paymentMethod])

  function updateForm(patch: Partial<CheckoutFormState>) {
    setForm((current) => ({ ...current, ...patch }))
  }

  function setBillingSameAsShipping(same: boolean) {
    setForm((current) => {
      if (
        !same &&
        isBillingFormEmpty(current)
      ) {
        return {
          ...current,
          billingSameAsShipping: false,
          billingName: current.recipientName,
          billingPhone: current.recipientPhone,
          billingAddress: shippingAddress,
          billingCity: location?.city ?? '',
          billingPincode: shippingPincode,
        }
      }
      return { ...current, billingSameAsShipping: same }
    })
  }

  function applyCoupon() {
    setCouponError(couponApplyMessage(couponInput))
  }

  function addLineAddon(lineId: string, addon: CheckoutAddonOption) {
    addAddon(lineId, {
      id: addon.id,
      label: addon.label,
      price: addon.price,
    })
  }

  function closeConfirmation() {
    setConfirmation(null)
    setSubmitting(false)
  }

  function closePlaceConfirm() {
    setPlaceConfirmOpen(false)
  }

  function selectSavedAddress(id: string | null) {
    if (!id) {
      setSelectedAddressId(null)
      updateForm({ address: '' })
      return
    }
    const address = addresses.find((item) => item.id === id)
    if (!address) return
    setSelectedAddressId(address.id)
    updateForm({
      recipientName: address.recipientName,
      recipientPhone: nationalPhoneDigits(address.recipientPhone),
      address: address.address,
    })
  }

  const savedAddressIds = savedAddresses.map((address) => address.id).join(',')
  useEffect(() => {
    if (selectedAddressId && !savedAddressIds.split(',').includes(selectedAddressId)) {
      setSelectedAddressId(null)
    }
  }, [savedAddressIds, selectedAddressId])

  function goToAddress() {
    if (step === CHECKOUT_STEP.done) return
    setError(null)
    setPlaceConfirmOpen(false)
    setStep(CHECKOUT_STEP.address)
  }

  function pincodeError(): string | null {
    if (pincodeResolving) return CHECKOUT_UI.pincodeResolving
    return isValidPincode(shippingPincode) ? null : CHECKOUT_UI.pincodeInvalid
  }

  function goToCheckout() {
    if (!location) {
      setError(CHECKOUT_UI.locationMissing)
      return false
    }
    const invalidPincode = pincodeError()
    if (invalidPincode) {
      setError(invalidPincode)
      return false
    }
    if (!isCheckoutFormReady(checkoutForm, shippingCity)) {
      setError(CHECKOUT_UI.validationError)
      return false
    }
    setError(null)
    setStep(CHECKOUT_STEP.checkout)
    return true
  }

  function selectStep(next: CheckoutStep) {
    if (next === CHECKOUT_STEP.address) {
      goToAddress()
      return
    }
    if (next === CHECKOUT_STEP.checkout) {
      goToCheckout()
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (step === CHECKOUT_STEP.address) {
      goToCheckout()
      return
    }
    await requestPlaceOrder()
  }

  async function requestPlaceOrder() {
    if (!validateCheckout()) return
    if (
      express?.requiresConfirmation &&
      !express.canSplit
    ) {
      setConfirmation(confirmationFromExpressCheck(express))
      return
    }
    setPlaceConfirmOpen(true)
  }

  async function confirmPlaceOrder() {
    setPlaceConfirmOpen(false)
    await submitOrder(false)
  }

  async function confirmStandardDelivery() {
    setConfirmation(null)
    await submitOrder(true)
  }

  function validateCheckout(): boolean {
    if (!location) {
      setError(CHECKOUT_UI.locationMissing)
      return false
    }
    if (lines.length === 0) return false
    const invalidPincode = pincodeError()
    if (invalidPincode) {
      setError(invalidPincode)
      return false
    }
    if (!isCheckoutFormReady(checkoutForm, shippingCity)) {
      setError(CHECKOUT_UI.validationError)
      return false
    }
    if (
      canSchedule &&
      scheduleMode === CHECKOUT_SCHEDULE_MODE.scheduled &&
      !scheduledSlot
    ) {
      setError(SCHEDULE_UI.required)
      return false
    }
    if (
      canScheduleExpress &&
      expressScheduleMode === CHECKOUT_SCHEDULE_MODE.scheduled &&
      !expressScheduledSlot
    ) {
      setError(SCHEDULE_UI.expressRequired)
      return false
    }
    return true
  }

  async function submitOrder(acceptStandardDelivery: boolean) {
    if (!validateCheckout() || !location) return

    if (
      !acceptStandardDelivery &&
      express?.requiresConfirmation &&
      !express.canSplit
    ) {
      setConfirmation(confirmationFromExpressCheck(express))
      return
    }

    const name = form.recipientName.trim()
    const phone = nationalPhoneDigits(form.recipientPhone)
    const address = shippingAddress
    const city = location.city
    const pincode = shippingPincode
    const lat = location.lat
    const lng = location.lng

    setSubmitting(true)
    setPlacingPhase(CHECKOUT_PLACING_PHASE.saving)
    setError(null)
    try {
      const payload: PlaceOrderInput = {
        items: cartLinesToOrderItems(lines),
        delivery: buildPlaceOrderDelivery({
          recipientName: name,
          recipientPhone: phone,
          address,
          city,
          pincode,
          notes: form.notes,
          lat: lat ?? undefined,
          lng: lng ?? undefined,
        }),
        billing: buildPlaceOrderBilling({
          sameAsShipping: form.billingSameAsShipping,
          name: form.billingName,
          phone: form.billingPhone,
          address: form.billingAddress,
          city: form.billingCity,
          pincode: form.billingPincode,
        }),
        ...(acceptStandardDelivery ? { acceptStandardDelivery: true } : {}),
        ...checkoutPlaceFlags({
          canSplit,
          splitMode,
          scheduleMode: canSchedule ? scheduleMode : CHECKOUT_SCHEDULE_MODE.asap,
          scheduledFor: scheduledSlot,
          expressScheduleMode: canScheduleExpress
            ? expressScheduleMode
            : CHECKOUT_SCHEDULE_MODE.asap,
          expressScheduledFor: expressScheduledSlot,
        }),
        ...(form.paymentMethod === PAYMENT_METHOD.online
          ? { paymentMethod: PAYMENT_METHOD.online }
          : {}),
      }
      const checkout = await placeOrder(payload)
      const session = sessionFromCheckout(checkout)
      let placedCheckout = checkout
      let settleStatus: (typeof CHECKOUT_SETTLE)[keyof typeof CHECKOUT_SETTLE] =
        CHECKOUT_SETTLE.paid

      if (session) {
        setPlacingPhase(CHECKOUT_PLACING_PHASE.paying)
        const placed = readCheckoutOrders(checkout)
        const timeouts = placed.map((order) => onlinePaymentTimeoutMs(order.createdAt))
        const timeoutMs = timeouts.length > 0 ? Math.min(...timeouts) : undefined
        const settled = await payOnlineCheckout({
          checkoutId: checkout.checkoutId,
          session,
          timeoutMs,
        })
        settleStatus = settled.status
        if (settled.checkout) placedCheckout = settled.checkout
      }

      let orders = readCheckoutOrders(placedCheckout)
      if (orders.length === 0) {
        throw new Error(CHECKOUT_UI.placeError)
      }

      if (settleStatus === CHECKOUT_SETTLE.expired) {
        orders = await cancelUnpaidOnlineOrders(orders)
        clear()
        for (const order of orders) {
          queryClient.setQueryData(QUERY_KEYS.order(order.id), order)
        }
        queryClient.setQueryData<ApiOrder[]>(QUERY_KEYS.orders, (current) =>
          prependOrders(current, orders),
        )
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orders })
        setPlacedOrders(orders)
        setStep(CHECKOUT_STEP.done)
        setPlacingPhase(null)
        setSubmitting(false)
        return
      }

      const awaitingPayment =
        settleStatus === CHECKOUT_SETTLE.unverified ||
        (settleStatus === CHECKOUT_SETTLE.paid && orders.some(needsPaymentConfirmation))

      if (awaitingPayment) {
        setPlacingPhase(CHECKOUT_PLACING_PHASE.confirming)
        orders = await waitForCheckoutPayment(orders, fetchOrder)
      }

      clear()
      for (const order of orders) {
        queryClient.setQueryData(QUERY_KEYS.order(order.id), order)
      }
      queryClient.setQueryData<ApiOrder[]>(QUERY_KEYS.orders, (current) =>
        prependOrders(current, orders),
      )
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orders })
      setPlacedOrders(orders)
      setStep(CHECKOUT_STEP.done)
      setPlacingPhase(null)
      setSubmitting(false)
    } catch (err) {
      const required = readExpressConfirmation(err)
      if (required && !acceptStandardDelivery) {
        setConfirmation(required)
        setPlacingPhase(null)
        setSubmitting(false)
        return
      }
      setError(err instanceof ApiError ? err.message : CHECKOUT_UI.placeError)
      setPlacingPhase(null)
      setSubmitting(false)
    }
  }

  return {
    form,
    updateForm,
    setBillingSameAsShipping,
    submitting,
    placingPhase,
    error,
    step,
    addressComplete,
    placedOrders,
    locationLabel: location?.label ?? null,
    shippingPincode,
    pincodeEditable,
    pincodeResolving,
    savedAddresses,
    selectedAddressId,
    selectSavedAddress,
    selectedAddress,
    city: location?.city ?? null,
    allowedPaymentMethod: settings.paymentMethod,
    couponInput,
    setCouponInput,
    couponError,
    applyCoupon,
    lineAddons,
    addLineAddon,
    shipments,
    canSplit,
    canSchedule,
    canScheduleExpress,
    expressItems,
    standardItems,
    deliveryEstimate,
    addressRecap,
    splitMode,
    setSplitMode,
    scheduleMode,
    setScheduleMode,
    scheduleDates,
    scheduledDate,
    setScheduledDate,
    scheduleSlots,
    scheduledSlot,
    setScheduledSlot,
    expressScheduleMode,
    setExpressScheduleMode,
    expressScheduleDates,
    expressScheduledDate,
    setExpressScheduledDate,
    expressScheduleSlots,
    expressScheduledSlot,
    setExpressScheduledSlot,
    onSubmit,
    goToAddress,
    goToCheckout,
    selectStep,
    express,
    expressChecking,
    confirmation,
    closeConfirmation,
    confirmStandardDelivery,
    placeConfirmOpen,
    closePlaceConfirm,
    confirmPlaceOrder,
  }
}

